// src/context/EasyCheckContext.js
import { createContext, useContext, useState, useEffect } from 'react';
import { easyCheckAuthService } from '../services/auth/authService';
import { userPermissionsService } from '../services/users/userPermissionsService';
import { getErrorMessage } from '../utils/errorHandler';
import PropTypes from 'prop-types';

const EasyCheckContext = createContext();

const EasyCheckProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [printQueue, setPrintQueue] = useState([]);

  // Initialize app state from localStorage
  useEffect(() => {
    const initializeApp = async () => {
      try {
        const token = localStorage.getItem('easyCheckToken');
        const userData = localStorage.getItem('easyCheckUser');

        if (token && userData) {
          // Restore user data without validation (since validateSession endpoint doesn't exist)
          setUser(JSON.parse(userData));
          console.log('🔐 User restored from localStorage on app refresh');
        }
      } catch (error) {
        console.error('EasyCheck app initialization error:', error);
        handleLogout();
      } finally {
        setLoading(false);
      }
    };

    initializeApp();
  }, []);

  const login = async (credentials) => {
    const result = await easyCheckAuthService.login(credentials);

    if (result.success) {
      // Check if OTP verification is needed
      if (result.requiresOtp) {
        return { ...result, requiresOtp: true };
      }

      const user = result.data.user || result.data;
      if (user && !user.requiresOtp) {
        setUser(user);
        userPermissionsService.clearCache();
        addNotification(`Welcome to EasyCheck, ${user.name}!`, 'success');
        return { ...result, redirectUrl: '/' };
      }
    }

    return result;
  };

  const verifyOtp = async ({ email, password, otp, trustDevice }) => {
    const result = await easyCheckAuthService.verifyOtp({ email, password, otp, trustDevice });

    if (result.success) {
      const user = result.data.user || result.data;
      setUser(user);
      userPermissionsService.clearCache();
      addNotification(`Welcome to EasyCheck, ${user.name}!`, 'success');
    }

    return result;
  };

  const resendOtp = async (email) => {
    return easyCheckAuthService.resendOtp(email);
  };

  const signup = async (userData) => {
    const result = await easyCheckAuthService.signup(userData);
    
    if (result.success) {
      addNotification('Account created successfully! Please check your email for verification.', 'success');
    } else {
      // Use the error handler to get user-friendly messages
      const errorMessage = result.message || getErrorMessage(result);
      addNotification(errorMessage, 'error');
    }
    
    return result;
  };

  const logout = async () => {
    await easyCheckAuthService.logout();
    handleLogout();
  };

  const handleLogout = () => {
    setUser(null);
    setPrintQueue([]);
    userPermissionsService.clearCache();

    // Clear localStorage
    localStorage.removeItem('easyCheckToken');
    localStorage.removeItem('easyCheckUser');
    localStorage.removeItem('easyCheckLoginTime');
    localStorage.removeItem('easyCheckRedirectUrl');

    addNotification('Logged out from EasyCheck', 'info');
  };

  const addNotification = (message, severity = 'info', autoHide = true) => {
    const id = Date.now();
    setNotifications(prev => [...prev, { id, message, severity }]);
    
    // Auto remove after 5 seconds for success/info notifications
    if (autoHide && ['success', 'info'].includes(severity)) {
      setTimeout(() => {
        removeNotification(id);
      }, 5000);
    }
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(notif => notif.id !== id));
  };

  // Print queue management
  const addToPrintQueue = (cheque) => {
    setPrintQueue(prev => [...prev, { ...cheque, queueId: Date.now() }]);
    addNotification('Cheque added to print queue', 'success');
  };

  const removeFromPrintQueue = (queueId) => {
    setPrintQueue(prev => prev.filter(item => item.queueId !== queueId));
  };

  const clearPrintQueue = () => {
    setPrintQueue([]);
    addNotification('Print queue cleared', 'info');
  };

  const value = {
    user,
    setUser,
    loading,
    notifications,
    printQueue,
    login,
    verifyOtp,
    resendOtp,
    signup,
    logout,
    addNotification,
    removeNotification,
    addToPrintQueue,
    removeFromPrintQueue,
    clearPrintQueue
  };

  return (
    <EasyCheckContext.Provider value={value}>
      {children}
    </EasyCheckContext.Provider>
  );
};

EasyCheckProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useEasyCheck = () => {
  const context = useContext(EasyCheckContext);
  if (!context) {
    throw new Error('useEasyCheck must be used within EasyCheckProvider');
  }
  return context;
};

export { EasyCheckProvider, EasyCheckContext }; 